/**
 * Moves focus to `<main>` after a client-side navigation.
 *
 * A full page load resets focus to the top of the document. A router navigation
 * does not: focus stays on the link that was just activated, which no longer
 * exists, so the next Tab starts from the top of the page and a screen reader
 * announces nothing at all. This is the single most common keyboard defect in
 * SPAs and it costs six lines to avoid.
 *
 * `preventScroll` because the router's own `scrollBehavior` has already decided
 * where the page should be, and focusing an element scrolls it into view.
 */

import { watch, type Ref } from 'vue'
import { useRoute } from 'vue-router'

export function useMainFocus (main: Ref<HTMLElement | null>): void {
  const route = useRoute()
  watch(
    () => route.fullPath,
    (_next, previous) => {
      // Skip the first render: the browser has just loaded the document and
      // stealing focus from wherever the user put it would be worse.
      if (previous === undefined) return
      main.value?.focus({ preventScroll: true })
    }
  )
}
